import { AbstractO2dElement, html, customElement, property } from '@o2d/shared';
import type { TemplateResult } from '@o2d/shared';
import type { PropertyValues } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { virtualize } from '@lit-labs/virtualizer/virtualize.js';
import type { O2dTableColumn } from '../types/table-column.type.js';
import type { O2dTableRowSizing } from '../types/table-row-sizing.type.js';
import type { O2dTableSort } from '../types/table-sort.type.js';
import { O2D_TABLE_SORT_CHANGED } from '../types/table-sort.type.js';
import { readAccessorValue } from '../controllers/column-accessor.js';
import { TableFilterController } from '../controllers/table-filter.controller.js';
import { TableSortController } from '../controllers/table-sort.controller.js';

/**
 * Lean, reusable data-table web component.
 *
 * Renders `rows` × `columns` as a CSS-grid (never a native `<table>`), extends
 * `AbstractO2dElement` (Light DOM), and exposes ARIA table semantics
 * (`role="table"`/`row`/`columnheader`/`cell`). Sort, filter, custom cell
 * renderers and opt-in virtualization are layered on in later increments.
 *
 * NB: no `TRow` generic — `row` is `unknown` throughout (see `O2dTableColumn`).
 */
@customElement('o2d-table')
export class O2dTableElement extends AbstractO2dElement {
    /** Column definitions (headers, widths, sort/filter accessors, renderers). */
    @property({ attribute: false })
    columns: O2dTableColumn[] = [];

    /** The rows to render. Opaque `unknown` — accessors cast internally. */
    @property({ attribute: false })
    rows: unknown[] = [];

    /** REQUIRED: stable per-row key for efficient `repeat`/virtualized reconciliation. */
    @property({ attribute: false })
    rowKey?: (row: unknown) => string;

    /** The single active sort (controlled-with-default). Wired in later increments. */
    @property({ attribute: false })
    sort?: O2dTableSort;

    /** The single global filter needle (OR-matched across columns). */
    @property({ type: String })
    filter = '';

    /** Opt-in DOM virtualization for large row counts. */
    @property({ type: Boolean })
    virtualize = false;

    /**
     * How body rows are sized: `'fixed'` (default) clips each row to
     * `var(--o2d-table-row-height, 2rem)`, `'content'` turns that into a
     * `min-height` floor so a row grows with its content. Supported together
     * with `virtualize` — the virtualizer measures its children.
     *
     * `attribute: 'row-sizing'` is REQUIRED, not decoration. Lit derives the
     * default attribute name by lowercasing the property
     * (`@lit/reactive-element` `reactive-element.js:470` returns
     * `name.toLowerCase()`), so without it this reflects as `rowsizing`, the
     * stylesheet's `[row-sizing="content"]` never matches, and the property
     * appears to work while changing nothing.
     */
    @property({ type: String, reflect: true, attribute: 'row-sizing' })
    rowSizing: O2dTableRowSizing = 'fixed';

    /** Provides the single global filter (OR-match across columns' `filterValue`). */
    private readonly _filterController = new TableFilterController(this);

    /** Provides the single active sort (order rows by the active column, asc↔desc). */
    private readonly _sortController = new TableSortController(this);

    /**
     * Optimistic local sort set by a header click. Reflected immediately so the
     * table re-orders without waiting for the consumer to write the `sort` prop
     * back. It is discarded the moment the consumer writes a new `sort` prop
     * (see `willUpdate`), so the `sort` prop stays the single source of truth.
     */
    private _localSort?: O2dTableSort;

    /**
     * Keeps the `sort` prop authoritative: whenever the consumer writes a new
     * `sort` prop, discard any optimistic local sort so the prop — the single
     * source of truth — wins over an earlier header click. On the first update
     * `_localSort` is already undefined, so clearing it is a no-op.
     */
    protected override willUpdate(changed: PropertyValues<this>): void {
        if (changed.has('sort')) {
            this._localSort = undefined;
        }
    }

    /** Renders the table container, sticky header row, and the body rows. */
    protected override render(): TemplateResult {
        this._assertRowKey();
        this._assertRowSizing();
        const gridColumns = this._gridTemplateColumns();
        const visibleRows = this._visibleRows();

        return html`
            <div class="o2d-table" role="table">
                <div class="o2d-table__header" role="row" style="grid-template-columns: ${gridColumns}">
                    ${this.columns.map(column => this._renderHeaderCell(column))}
                </div>
                <div class="o2d-table__body">
                    ${this._renderBody(visibleRows, gridColumns)}
                </div>
            </div>
        `;
    }

    /**
     * Renders the body rows through ONE shared `rowTemplate` (`_renderRow`) so the
     * `virtualize()` and `repeat()` paths emit byte-identical `.o2d-table__row`
     * markup — there is a single row-rendering code path, never a per-mode fork.
     *
     * When `virtualize=true` the `virtualize` directive hosts the rows on the inner
     * `.o2d-table__body` div (a CHILD of the outer scroll wrapper, so the wrapper —
     * never the host — is the resolved scroller); otherwise `repeat()` renders them
     * eagerly. Both paths reconcile by `rowKey`. NO `scroller: true` — the outer
     * wrapper (see `table.css`) is the scroll container.
     *
     * lit/lit#3362 describes `@lit-labs/virtualizer` mis-positioning the scroll thumb
     * when items grow AFTER first measurement. Under `row-sizing="content"` rows DO
     * grow, and the measured effect against 2.1.1 is an imprecise scroll TOTAL which
     * converges as rows are measured — not displacement of the content in view.
     */
    private _renderBody(visibleRows: unknown[], gridColumns: string): unknown {
        const rowTemplate = (row: unknown): TemplateResult => this._renderRow(row, gridColumns);
        return this.virtualize
            ? virtualize({ items: visibleRows, keyFunction: this.rowKey!, renderItem: rowTemplate })
            : repeat(visibleRows, this.rowKey!, rowTemplate);
    }

    /**
     * Derives the rows to render from `rows`: filter first, then sort.
     */
    private _visibleRows(): unknown[] {
        const filtered = this._filterController.filter(this.rows, this.filter, this.columns);
        return this._sortController.sort(filtered, this._activeSort(), this.columns);
    }

    /**
     * The sort currently applied. An optimistic header click (`_localSort`) wins
     * only until the consumer writes the `sort` prop, at which point `willUpdate`
     * clears `_localSort` and the prop takes over as the single source of truth.
     */
    private _activeSort(): O2dTableSort | undefined {
        return this._localSort ?? this.sort;
    }

    /**
     * Sortable-header click: toggles the active sort (new column → asc, same
     * column flips asc↔desc), applies it optimistically, and fires the typed
     * `o2d-table.sort-changed` event so the consumer can control the `sort` prop.
     */
    private _onHeaderClick(column: O2dTableColumn): void {
        if (!column.sortable) {
            return;
        }
        const next = this._sortController.toggle(column.id, this._activeSort());
        this._localSort = next;
        this.requestUpdate();
        this.fireEvent(O2D_TABLE_SORT_CHANGED, next);
    }

    /**
     * Fail-fast guard: rows cannot be stably keyed without a `rowKey` accessor.
     * Throws a readable error BEFORE `repeat()` (which would otherwise silently
     * fall back to unstable index-keying).
     */
    private _assertRowKey(): void {
        if (this.rows.length > 0 && !this.rowKey) {
            throw new Error('o2d-table: `rowKey` is required');
        }
    }

    /**
     * Fail-fast guard: a value outside the `O2dTableRowSizing` union matches no
     * CSS selector, so the table would render FIXED with no symptom at all. The
     * declared union does not describe what a consumer can actually put in the
     * attribute — an HTML attribute never passes through TypeScript — so the
     * runtime check is the only thing standing between `row-sizing="Content"`
     * and a silently wrong render.
     *
     * Three details are load-bearing and each was measured:
     *
     * 1. The widened local is NOT cosmetic. Comparing `this.rowSizing` (declared
     *    `O2dTableRowSizing`) against a literal outside that union raises
     *    `error TS2367`, and it fails at the worst moment: every test stays green
     *    and `npm run build` exits 0 WHILE printing the diagnostic. Assigning to
     *    a widened local is what lets the runtime check exist at all.
     * 2. `value != null` — NOT plain truthiness. Lit's `String` converter writes
     *    `null` into the property when the attribute is REMOVED, which is the
     *    ordinary way back to the default and never an error; a strict check
     *    throws there with the nonsense message `invalid row-sizing="null"`.
     *    Plain `if (value && …)` would carve out `''` too, and `''` is exactly
     *    what HTML gives a BARE `<o2d-table row-sizing>` — someone plainly
     *    meaning "turn this on" would get a silent fixed render.
     * 3. There is deliberately NO check involving `virtualize`. `content`
     *    together with `virtualize` is a SUPPORTED combination (the virtualizer
     *    measures its children and re-measures on resize); a component test
     *    exists solely to catch that prohibition being reintroduced.
     */
    private _assertRowSizing(): void {
        const value: string | null | undefined = this.rowSizing;
        if (value != null && value !== 'fixed' && value !== 'content') {
            throw new Error(
                `o2d-table: invalid row-sizing="${value}" — expected "fixed" or "content".`
            );
        }
    }

    /** Builds the `grid-template-columns` track list from each column's `width` (default `1fr`). */
    private _gridTemplateColumns(): string {
        return this.columns.map(column => column.width ?? '1fr').join(' ');
    }

    /** Renders a single header cell (sortable columns are clickable and expose `aria-sort`). */
    private _renderHeaderCell(column: O2dTableColumn): TemplateResult {
        return html`
            <div
                class="o2d-table__th"
                role="columnheader"
                data-col-id=${column.id}
                aria-sort=${ifDefined(this._ariaSort(column))}
                @click=${() => this._onHeaderClick(column)}
            >
                ${column.header}
            </div>
        `;
    }

    /** The `aria-sort` value for a column: set only on the active sortable column, else undefined. */
    private _ariaSort(column: O2dTableColumn): 'ascending' | 'descending' | undefined {
        const activeSort = this._activeSort();
        if (!column.sortable || activeSort?.columnId !== column.id) {
            return undefined;
        }
        return activeSort.direction === 'asc' ? 'ascending' : 'descending';
    }

    /**
     * The single shared row template used by BOTH the `repeat()` and `virtualize()`
     * paths (via `_renderBody`) — one cell per column, ARIA `role="row"`/`cell`.
     *
     * The `.o2d-table__row` class enforces the fixed EXACT row-height contract in
     * `table.css` by DEFAULT (`height` == `max-height`, `overflow: hidden`, no
     * `min-height` floor); `row-sizing="content"` lifts that cap and turns
     * `--o2d-table-row-height` into a floor instead. The default lets the virtualizer
     * size the scroller from one known row height; under content sizing it measures
     * rows itself, so a tall `cellRenderer` grows the row rather than being clipped.
     */
    private _renderRow(row: unknown, gridColumns: string): TemplateResult {
        return html`
            <div class="o2d-table__row" role="row" style="grid-template-columns: ${gridColumns}">
                ${this.columns.map(column => this._renderCell(row, column))}
            </div>
        `;
    }

    /**
     * Renders a single body cell. A column's `cellRenderer(row, column)` — when
     * present — fully controls the cell content; otherwise the default is the text
     * of the column's `filterValue` (safe-read, so a column without `filterValue`
     * renders empty rather than throwing).
     */
    private _renderCell(row: unknown, column: O2dTableColumn): TemplateResult {
        const content = column.cellRenderer
            ? column.cellRenderer(row, column)
            : readAccessorValue(column.filterValue, row);
        return html`<div class="o2d-table__cell" role="cell">${content}</div>`;
    }
}
